import { useState, useCallback } from "react";
import type { LayoutChangeEvent } from "react-native";
import { Text, View } from "@src/components";
import { formatDateTimeDmy } from "@src/utils/format-date";
import type { WasteSenseReportMeta } from "@visioni/a3s-reports/client-messages";

/**
 * Width (px) of the hero container below which the two side-by-side columns
 * stack into a single left-aligned column. This is measured against the hero's
 * OWN width (via onLayout), not the browser window — the report sits inside a
 * layout with a sidebar and optional right panel, so the window width tells us
 * nothing useful about how much room this block actually has.
 */
const STACK_BELOW_WIDTH = 700;

interface WasteSenseHeroProps {
  meta: WasteSenseReportMeta;
  /**
   * ISO instant the report row was last marked ready (the generator's
   * `generatedAt`). Reports regenerate daily, so this is the "last updated"
   * freshness signal. Optional/absent for a not-yet-ready row — the line is
   * omitted rather than shown blank in that case.
   */
  generatedAt?: string;
}

/**
 * Top-of-report header block. All copy comes pre-formatted from the
 * generator (`report_meta`) so the renderer never has to reconstruct
 * the title or window from raw bounds — the same strings appear in
 * the static PDF, the email digest, and this screen.
 */
export function WasteSenseHero({ meta, generatedAt }: WasteSenseHeroProps) {
  const generatedAtLabel = formatDateTimeDmy(generatedAt);

  // Measure the hero's actual rendered width so the breakpoint is a true
  // container query. `undefined` until the first layout pass — we default to
  // the wide layout in that window because the hero renders full-width and only
  // collapses once measured narrow, which avoids a flash of the stacked layout
  // in the common desktop case.
  const [containerWidth, setContainerWidth] = useState<number | undefined>(undefined);
  const onLayout = useCallback((e: LayoutChangeEvent) => {
    setContainerWidth(e.nativeEvent.layout.width);
  }, []);

  // Stack (4 rows, all left-aligned) once the container is narrower than the
  // threshold. When stacked we also drop the type scale a step and tighten the
  // line gap so the taller single column doesn't dominate the report.
  const isStacked = containerWidth !== undefined && containerWidth < STACK_BELOW_WIDTH;

  const titleStyle = isStacked ? "h4" : "h3";
  const lineStyle = isStacked ? "xs/regular" : "sm/regular";
  const lineGap = isStacked ? "xs" : "sm";

  return (
    <View
      onLayout={onLayout}
      // Row on wide containers (left/right columns) so the four lines only take
      // two rows of height; column when stacked so everything flows down.
      flexDirection={isStacked ? "column" : "row"}
      gap={isStacked ? "xs" : "3xl"}
      alignItems="flex-start"
      // Full width so onLayout reports the container's real width, and so the
      // flex-push below has the full row to spread across.
      width="100%"
    >
      <View gap={lineGap} alignItems="flex-start" flex={isStacked ? undefined : 1}>
        <Text textStyle={titleStyle} color="text">
          {meta.report_title}
        </Text>
        <Text textStyle={lineStyle} color="textMuted">
          {meta.report_subtitle}
        </Text>
      </View>
      <View gap={lineGap} alignItems={isStacked ? "flex-start" : "flex-end"}>
        <Text textStyle={lineStyle} color="text">
          Reporting window: {meta.window_label}
        </Text>
        {generatedAtLabel ?
          <Text textStyle={lineStyle} color="textMuted">
            Generated at: {generatedAtLabel}
          </Text>
        : null}
      </View>
    </View>
  );
}
